import { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  LogOut,
  ChevronDown,
  ChevronRight,
  Home,
  Search,
  Plus,
  Sparkles,
  Zap,
} from 'lucide-react';
import { AuthStore } from '../../stores';
import logo from '../../assets/Aiquris Logo.png';
import logoIcon from '../../assets/AIQURIS Logo - no name.png';
import './AppLayout.css';
import type { User } from '../../entities/user/User';
import { getAuthService } from '../../services/auth.service';
import { ImpactPlusRegisterHeaderStore } from '../../stores/ImpactPlusRegisterHeaderStore';
import { DashboardStore } from '../../stores/DashboardStore';
import { getImpactPlusUseCaseTitleForSidebar } from '../../pages/impact-plus/impactRegisterDummyData';
// import UserGuideTour from '../UserGuideTour/UserGuideTour';

type AppLayoutProps = {
  children: React.ReactNode;
};

const InfoIcon = () => (
  <svg width="40" height="40" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="28" fill="var(--slate-gray)" />
    <circle cx="32" cy="32" r="15" stroke="var(--deep-blue)" strokeWidth="4" />
    <circle cx="32" cy="24" r="2.5" fill="var(--deep-blue)" />
    <rect x="29.5" y="28" width="5" height="12" rx="2.5" fill="var(--deep-blue)" />
  </svg>
);

const SIDEBAR_WIDTH = 252;
const SIDEBAR_COLLAPSED_WIDTH = 72;

function deriveImpactUseCaseId(pathname: string): string | null {
  const m = /^\/use-case\/([^/]+)/.exec(pathname);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

const USE_CASE_CRUMB_LABELS: Record<string, string> = {
  '': 'Use Case Overview',
  hazards: 'Impact Overview',
  'stakeholder-register': 'Stakeholder Register',
  'impact-assessment': 'Stakeholder Impact Assessment',
  executive: 'Risk Overview',
  'deployment-risk-profile': 'Risk Domains',
  'risk-findings': 'Risk Register',
  'risk-controls': 'Required Controls',
};

function deriveUseCaseCrumbLabel(pathname: string): string {
  const m = /^\/use-case\/[^/]+\/?([^/?]*)/.exec(pathname);
  const section = m?.[1] ?? '';
  return USE_CASE_CRUMB_LABELS[section] ?? 'Use Case Overview';
}

function deriveUseCaseSidebarLabel(rawTitle?: string | null): string {
  const t = String(rawTitle ?? '').trim();
  return t || 'Use case';
}

export default function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = AuthStore.useAuthStore((state) => state.user);
  const logout = AuthStore.useAuthStore((state) => state.logout);
  const getLicense = AuthStore.useAuthStore((state) => state.getLicense);
  const license = AuthStore.useAuthStore((state) => state.license);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const [userInfo, setUserInfo] = useState<User | null>(null);
  const [isLoadingUserInfo, setIsLoadingUserInfo] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const isCreatingRef = useRef(false);

  const isUseCaseRegisterPage = location.pathname === '/dashboard';

  const registerSearchQuery = ImpactPlusRegisterHeaderStore((s) => s.registerSearchQuery);
  const setRegisterSearchQuery = ImpactPlusRegisterHeaderStore((s) => s.setRegisterSearchQuery);
  const createNewHandler = ImpactPlusRegisterHeaderStore((s) => s.createNewHandler);

  const impactUseCaseId = useMemo(
    () => deriveImpactUseCaseId(location.pathname),
    [location.pathname],
  );
  const useCaseBriefs = DashboardStore.useDashboardStore((s) => s.useCaseBriefs);
  const loadUseCaseBriefs = DashboardStore.useDashboardStore((s) => s.loadUseCaseBriefs);
  const impactPlusReportQuery = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const fromUrl = sp.get('impactPlusReportId') ?? '';
    if (fromUrl) return `?impactPlusReportId=${encodeURIComponent(fromUrl)}`;

    // Fallback: pick the first Impact+ report from the brief so sidebar links
    // stay populated even when the URL doesn't carry the report ID.
    if (impactUseCaseId) {
      const brief = useCaseBriefs.find((b) => b.useCaseChatId === impactUseCaseId);
      const impactReport = (brief?.reports ?? []).find((r) =>
        /impactplus/i.test(String(r.reportType ?? '')) ||
        /\bimpact\+/i.test(String(r.reportTitle ?? ''))
      );
      if (impactReport?.reportId) {
        return `?impactPlusReportId=${encodeURIComponent(impactReport.reportId)}`;
      }
    }
    return '';
  }, [location.search, impactUseCaseId, useCaseBriefs]);
  const selectedUseCaseTitle = useMemo(
    () => getImpactPlusUseCaseTitleForSidebar(impactUseCaseId, useCaseBriefs),
    [impactUseCaseId, useCaseBriefs],
  );
  const crumbLabel = useMemo(() => {
    if (impactUseCaseId) return deriveUseCaseCrumbLabel(location.pathname);
    if (location.pathname === '/settings') return 'Settings';
    if (location.pathname.startsWith('/chat')) return 'New Use Case';
    return '';
  }, [impactUseCaseId, location.pathname]);
  useEffect(() => {
    const onTourLayout = (e: Event) => {
      const ce = e as CustomEvent<{
        sidebarOpen?: boolean;
        sidebarCollapsed?: boolean;
      }>;
      const d = ce.detail || {};
      if (typeof d.sidebarOpen === 'boolean') setSidebarOpen(d.sidebarOpen);
      if (typeof d.sidebarCollapsed === 'boolean') setSidebarCollapsed(d.sidebarCollapsed);
    };

    window.addEventListener('tour:layout', onTourLayout as EventListener);
    return () => window.removeEventListener('tour:layout', onTourLayout as EventListener);
  }, []);

  useEffect(() => {
    if (!impactUseCaseId) return;
    if (useCaseBriefs.length > 0) return;
    void loadUseCaseBriefs(true).catch(() => { });
  }, [impactUseCaseId, useCaseBriefs.length, loadUseCaseBriefs]);

  useEffect(() => {
    getLicense().catch((err) => {
      console.error('Unable to fetch license details', err.message);
    });
  }, [getLicense]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!user?.id) return;
      try {
        setIsLoadingUserInfo(true);
        const authService = getAuthService();
        const udata = await authService.getUser();
        setUserInfo(udata);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
        setUserInfo(null);
      } finally {
        setIsLoadingUserInfo(false);
      }
    };
    fetchUserInfo();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setProfileOpen(false);
    logout();
    navigate('/login');
  };

  const getDisplayName = () => {
    if (userInfo?.firstName && userInfo?.lastName) {
      return `${userInfo.firstName} ${userInfo.lastName}`.trim();
    }
    return 'User';
  };

  const getInitials = () => {
    if (userInfo?.firstName && userInfo?.lastName) {
      return (userInfo.firstName[0] + userInfo.lastName[0]).toUpperCase();
    }
    if (userInfo?.firstName) {
      return userInfo.firstName.slice(0, 2).toUpperCase();
    }
    if (userInfo?.lastName) {
      return userInfo.lastName.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  const closeSidebar = () => setSidebarOpen(false);

  const handleLogoClick = () => {
    closeSidebar();
    if (/\/chat\/[^/]+\/(new-impact|edit)/.test(window.location.pathname)) {
      toast.info('Your progress has been saved. Redirecting to the Use Case Register.', { icon: <InfoIcon /> });
    }
  };

  const navSections = [
    {
      label: 'Overview',
      items: [{ to: '/dashboard', end: false, icon: 'grid_view', label: 'Use Case Register' }],
    },
    {
      label: 'Organisation',
      items: [{ to: '/settings', end: false, icon: 'settings', label: 'Settings' }],
    },
  ];

  return (
    <div className="flex h-screen w-full overflow-hidden bg-gray-50">
      {sidebarOpen && (
        <div
          className="applayout-overlay fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`
          applayout-sidebar applayout-sidebar--dark fixed inset-y-0 left-0 z-50 flex flex-col
          border-r border-sidebar-line bg-white shadow-xl
          transition-[width,transform] duration-200 ease-out
          lg:relative lg:z-auto lg:translate-x-0 lg:shadow-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ width: sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH }}
      >
        <div className="relative flex shrink-0 items-center border-b border-sidebar-line px-5 pb-[15px] pt-[18px]">
          <NavLink
            to="/dashboard"
            className={`flex flex-1 items-center overflow-hidden ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}
            onClick={handleLogoClick}
          >
            <img
              src={sidebarCollapsed ? logoIcon : logo}
              alt="AIQURIS"
              className="h-[29px] w-auto shrink-0"
            />
          </NavLink>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden rounded-lg p-2 text-gray-500 hover:bg-gray-100"
            aria-label="Close sidebar"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-3">
          {navSections.map((section) => (
            <div key={section.label}>
              {!sidebarCollapsed && (
                <div className="applayout-nav-heading mt-2.5 px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-[.09em] text-sidebar-muted first:mt-0">
                  {section.label}
                </div>
              )}
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <li key={item.to + item.label}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      onClick={closeSidebar}
                      className={({ isActive }) =>
                        `applayout-nav-item flex items-center rounded-lg px-2.5 py-2 text-[12.5px] leading-[1.2] ${sidebarCollapsed ? 'justify-center' : 'gap-[11px]'
                        } ${isActive
                          ? 'applayout-nav-item--active'
                          : 'text-sidebar-text hover:bg-sidebar-hover hover:text-midnight-navi'
                        }`
                      }
                      title={sidebarCollapsed ? item.label : undefined}
                    >
                      <span className="aq-icon w-5 shrink-0 text-center" style={{ fontSize: 19 }} aria-hidden>
                        {item.icon}
                      </span>
                      {!sidebarCollapsed && <span>{item.label}</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>

              {section.label === 'Overview' &&
                !sidebarCollapsed &&
                impactUseCaseId && (
                  <div className="mt-1.5">
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard')}
                      className="flex items-center gap-1.5 px-2.5 pb-2 pt-[5px] text-[10.5px] font-normal text-sidebar-muted hover:text-sidebar-text"
                    >
                      <span className="aq-icon shrink-0" style={{ fontSize: 15 }} aria-hidden>arrow_back</span>
                      Back to Use Case Register
                    </button>
                    <div className="px-2.5 pb-2 pt-0.5 text-[12.5px] font-semibold leading-[1.35] text-midnight-navi">
                      {deriveUseCaseSidebarLabel(selectedUseCaseTitle)}
                    </div>

                    <ul className="space-y-0.5">
                      {/* Overview */}
                      <li>
                        <NavLink
                          to={`/use-case/${encodeURIComponent(impactUseCaseId)}${impactPlusReportQuery}`}
                          end
                          onClick={closeSidebar}
                          className={({ isActive }) =>
                            `applayout-nav-item flex items-center gap-[11px] rounded-lg px-2.5 py-2 text-[12.5px] leading-[1.2] ${isActive
                              ? 'applayout-nav-item--active'
                              : 'text-sidebar-text hover:bg-sidebar-hover hover:text-midnight-navi'
                            }`
                          }
                        >
                          <span className="aq-icon w-5 shrink-0 text-center" style={{ fontSize: 19 }} aria-hidden>overview</span>
                          <span>Use Case Overview</span>
                        </NavLink>
                      </li>
                    </ul>

                    {/* Impact+ */}
                    <div className="applayout-nav-heading mt-2.5 px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-[.09em] text-sidebar-muted">
                      Impact+
                    </div>
                    <ul className="space-y-0.5">
                      <li>
                        <NavLink
                          to={`/use-case/${encodeURIComponent(impactUseCaseId)}/hazards${impactPlusReportQuery}`}
                          end={false}
                          onClick={closeSidebar}
                          className={({ isActive }) =>
                            `applayout-nav-item flex items-center gap-[11px] rounded-lg px-2.5 py-2 text-[12.5px] leading-[1.2] ${isActive
                              ? 'applayout-nav-item--active'
                              : 'text-sidebar-text hover:bg-sidebar-hover hover:text-midnight-navi'
                            }`
                          }
                        >
                          <span className="aq-icon w-5 shrink-0 text-center" style={{ fontSize: 19 }} aria-hidden>radar</span>
                          <span>Impact Overview</span>
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to={`/use-case/${encodeURIComponent(impactUseCaseId)}/stakeholder-register${impactPlusReportQuery}`}
                          end={false}
                          onClick={closeSidebar}
                          data-tour="sidebar-stakeholder-register"
                          id="nav-stak"
                          className={({ isActive }) =>
                            `applayout-nav-item flex items-center gap-[11px] rounded-lg px-2.5 py-2 text-[12.5px] leading-[1.2] ${isActive
                              ? 'applayout-nav-item--active'
                              : 'text-sidebar-text hover:bg-sidebar-hover hover:text-midnight-navi'
                            }`
                          }
                        >
                          <span className="aq-icon w-5 shrink-0 text-center" style={{ fontSize: 19 }} aria-hidden>groups</span>
                          <span>Stakeholder Register</span>
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to={`/use-case/${encodeURIComponent(impactUseCaseId)}/impact-assessment${impactPlusReportQuery}`}
                          end={false}
                          onClick={closeSidebar}
                          data-tour="sidebar-impact-assessment"
                          id="nav-impact"
                          className={({ isActive }) =>
                            `applayout-nav-item flex items-center gap-[11px] rounded-lg px-2.5 py-2 text-[12.5px] leading-[1.2] ${isActive
                              ? 'applayout-nav-item--active'
                              : 'text-sidebar-text hover:bg-sidebar-hover hover:text-midnight-navi'
                            }`
                          }
                        >
                          <span className="aq-icon w-5 shrink-0 text-center" style={{ fontSize: 19 }} aria-hidden>warning</span>
                          <span>Stakeholder Impact Assessment</span>
                        </NavLink>
                      </li>
                    </ul>

                    {/* Risk+ */}
                    <div className="applayout-nav-heading mt-2.5 px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-[.09em] text-sidebar-muted">
                      Risk+
                    </div>
                    <ul className="space-y-0.5">
                      {[
                        { label: 'Risk Overview', path: 'executive', icon: 'gpp_maybe' },
                        { label: 'Risk Domains', path: 'deployment-risk-profile', icon: 'hub' },
                        { label: 'Risk Register', path: 'risk-findings', icon: 'checklist' },
                        { label: 'Required Controls', path: 'risk-controls', icon: 'shield' },
                      ].map(({ label, path, icon }) => (
                        <li key={path}>
                          <NavLink
                            to={`/use-case/${encodeURIComponent(impactUseCaseId)}/${path}`}
                            end={false}
                            onClick={closeSidebar}
                            className={({ isActive }) =>
                              `applayout-nav-item flex items-center gap-[11px] rounded-lg px-2.5 py-2 text-[12.5px] leading-[1.2] ${isActive
                                ? 'applayout-nav-item--active'
                                : 'text-sidebar-text hover:bg-sidebar-hover hover:text-midnight-navi'
                              }`
                            }
                          >
                            <span className="aq-icon w-5 shrink-0 text-center" style={{ fontSize: 19 }} aria-hidden>
                              {icon}
                            </span>
                            <span>{label}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>
          ))}
        </nav>

        <div className="shrink-0 border-t border-sidebar-line p-2">
          <div className="relative w-full" ref={profileRef}>
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              data-tour="profile-credits"
              id="sb-footer-anchor"
              className={`applayout-profile-btn flex items-center rounded-xl border border-sidebar-line bg-white hover:border-gray-300 ${sidebarCollapsed
                ? 'w-full flex-col gap-2 p-2'
                : 'w-full gap-2.5 p-[9px]'
                }`}
              aria-expanded={profileOpen}
              aria-haspopup="true"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-light-sky-blue bg-royal-blue text-xs font-semibold text-white">
                {getInitials()}
              </div>
              {!sidebarCollapsed ? (
                <>
                  <div className="min-w-0 flex-1 text-left">
                    <div className="text-xs font-semibold leading-[1.2] text-midnight-navi">
                      {isLoadingUserInfo ? '...' : getDisplayName()}
                    </div>
                    <div className="max-w-[150px] truncate text-[10px] font-normal text-sidebar-muted">{userInfo?.username || ''}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <ChevronDown
                      size={16}
                      className={`shrink-0 text-sidebar-muted transition-transform ${profileOpen ? 'rotate-180' : ''}`}
                    />
                  </div>
                </>
              ) : null}
            </button>

            {profileOpen && (
              <div
                className={`applayout-dropdown absolute z-50 w-64 rounded-xl border border-gray-200 bg-white py-1 ${sidebarCollapsed
                  ? 'bottom-full left-1/2 mb-2 -translate-x-1/2'
                  : 'bottom-full left-0 mb-2'
                  }`}
              >
                <div className="border-b border-gray-100 px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">
                    {isLoadingUserInfo ? '...' : getDisplayName()}
                  </div>
                  <div className="truncate text-xs text-gray-500">{userInfo?.username || ''}</div>
                </div>
                <div className="py-1">
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600"
                  >
                    <LogOut size={16} />
                    Log out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 min-h-16 shrink-0 items-center gap-2 border-b border-gray-200/80 bg-white px-4 shadow-sm md:gap-4 md:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden rounded-lg p-2 text-gray-600 hover:bg-gray-100"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
          <button
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            className="hidden lg:flex rounded-lg p-2 text-gray-600 hover:bg-gray-100"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Menu size={22} />
          </button>
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {isUseCaseRegisterPage ? (
              <>
                <div className="relative min-w-0 w-[min(100%,15rem)] sm:w-48 md:w-60 lg:w-72 shrink-0">
                  <Search
                    className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
                    aria-hidden
                  />
                  <input
                    type="search"
                    value={registerSearchQuery}
                    onChange={(e) => setRegisterSearchQuery(e.target.value)}
                    placeholder="Search use cases..."
                    className="w-full rounded-[8px] border border-gray-200/90 bg-impact-plus-page py-1.5 pl-8 pr-2.5 text-xs text-gray-800 placeholder:text-gray-500 shadow-inner shadow-gray-200/40 outline-none ring-0 transition-[box-shadow,border-color] focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                    aria-label="Search use cases"
                  />
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-3">
                  <button type="button" onClick={() => navigate('/settings')} className="inline-flex items-center gap-2 rounded-[8px] border border-gray-200 px-2.5 py-1.5 text-xs font-semibold text-gray-700 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-100" style={{ backgroundColor: 'var(--soft-gray)' }}>
                    <Sparkles size={13} className="text-royal-blue shrink-0" />
                    <span className="whitespace-nowrap">Impact+ {license.impactPlusReportCredits}</span>
                  </button>
                  <button type="button" onClick={() => navigate('/settings')} className="inline-flex items-center gap-2 rounded-[8px] border border-gray-200 px-2.5 py-1.5 text-xs font-semibold text-gray-700 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-100" style={{ backgroundColor: 'var(--soft-gray)' }}>
                    <Sparkles size={13} className="text-royal-blue shrink-0" />
                    <span className="whitespace-nowrap">Risk+ {license.riskPlusReportCredits}</span>
                  </button>
                  <div className="h-6 w-px bg-gray-300 shrink-0" aria-hidden />
                  <button
                    id="topbar-create"
                    type="button"
                    onClick={() => {
                      if (isCreatingRef.current || !createNewHandler) return;
                      isCreatingRef.current = true;
                      void Promise.resolve(createNewHandler()).finally(() => {
                        isCreatingRef.current = false;
                      });
                    }}
                    className="inline-flex shrink-0 items-center justify-center gap-1 rounded-[8px] bg-midnight-navi px-2.5 py-1.5 text-xs font-semibold text-white shadow shadow-midnight-navi/15 transition-all hover:bg-assured-dark-blue md:gap-1.5 md:px-3"
                  >
                    <Plus size={14} strokeWidth={2.5} className="shrink-0" />
                    <span className="whitespace-nowrap">Create new use case</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex min-w-0 flex-1 items-center gap-[7px] text-[12.5px] font-medium text-sidebar-muted">
                  <Home size={16} className="shrink-0 text-aiquris-blue-200" aria-hidden />
                  {impactUseCaseId && (
                    <>
                      <span className="truncate">{deriveUseCaseSidebarLabel(selectedUseCaseTitle)}</span>
                      <ChevronRight size={16} className="shrink-0 text-aiquris-blue-200" aria-hidden />
                    </>
                  )}
                  <span className="truncate font-semibold text-midnight-navi">{crumbLabel}</span>
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-3">
                  <div className="flex shrink-0 items-center gap-2">
                    <button type="button" onClick={() => navigate('/settings')} className="inline-flex items-center gap-1.5 rounded-[8px] bg-soft-gray px-2.5 py-1.5 text-xs font-medium leading-none text-midnight-navi hover:bg-gray-100">
                      <Zap size={15} className="text-royal-blue shrink-0" />
                      <span>Impact+</span>
                      <span className="font-semibold">{license.impactPlusReportCredits}</span>
                    </button>
                    <button type="button" onClick={() => navigate('/settings')} className="inline-flex items-center gap-1.5 rounded-[8px] bg-soft-gray px-2.5 py-1.5 text-xs font-medium leading-none text-midnight-navi hover:bg-gray-100">
                      <Zap size={15} className="text-royal-blue shrink-0" />
                      <span>Risk+</span>
                      <span className="font-semibold">{license.riskPlusReportCredits}</span>
                    </button>
                  </div>
                  <div className="h-[22px] w-px shrink-0 bg-sidebar-line" aria-hidden />
                  <button
                    type="button"
                    onClick={() => {
                      if (createNewHandler) {
                        if (isCreatingRef.current) return;
                        isCreatingRef.current = true;
                        void Promise.resolve(createNewHandler()).finally(() => {
                          isCreatingRef.current = false;
                        });
                        return;
                      }
                      navigate('/dashboard');
                    }}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-[8px] bg-assured-dark-blue px-3 py-2 text-xs font-medium text-white hover:bg-midnight-navi"
                  >
                    <Plus size={14} strokeWidth={2.5} className="shrink-0" />
                    <span className="whitespace-nowrap">Create new use case</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          {children}
          {/* Toggle this boolean to switch tour mode:
              true  => 5-step blank tour
              false => 8-step populated tour */}
          {/* <UserGuideTour forceBlankTour={false} /> */}
        </main>
      </div>
    </div>
  );
}
